'use client';
import { cn } from '../../lib/utils';
import { Avatar } from './Avatar';
import { AVATAR_STACK, AVATAR_STACK_CHIP, AVATAR_STACK_RING, AVATAR_STACK_SIZE } from './avatarStack';

type AvatarStackUser = Parameters<typeof Avatar>[0]['user'];

/** The overlapping row of round faces: the stack geometry of `avatarStack.ts` around the shared `Avatar`, so
 *  every group of people overlaps, rings and sizes alike. Each face keeps the `Avatar`'s own name. With
 *  `max`, the faces past it fold into one "+N" chip of the same diameter; `overflowLabel` names that chip
 *  (it is then an image to a screen reader), without it the chip is decorative because the caller states
 *  the count elsewhere. `className` places the row and never paints it.
 *
 *  Consumers: a project's team on its card (`ProjectTeamStrip`), the memory sharers in the project drawer
 *  (`ProjectDrawer`), and plugin bundles through `ElowenUiRuntime.components.AvatarStack`. A row whose faces
 *  each need their own tooltip and focus stop is a `MarkStrip` of avatars instead. */
export function AvatarStack({ users, size = AVATAR_STACK_SIZE, max, overflowLabel, className }: {
  users: readonly AvatarStackUser[];
  size?: number;
  max?: number;
  overflowLabel?: string;
  className?: string;
}) {
  const shown = max === undefined ? users : users.slice(0, max);
  const hidden = users.length - shown.length;
  return (
    <span data-avatar-stack className={cn('flex shrink-0', AVATAR_STACK, className)}>
      {shown.map((user) => (
        <span key={user.id} className={AVATAR_STACK_RING}><Avatar user={user} size={size} /></span>
      ))}
      {hidden > 0 ? (
        <span
          {...(overflowLabel ? { role: 'img', 'aria-label': overflowLabel } : { 'aria-hidden': true })}
          className={`${AVATAR_STACK_CHIP} text-small-emphasis tabular-nums`}
          style={{ width: size, height: size }}
        >
          +{hidden}
        </span>
      ) : null}
    </span>
  );
}
